/** Extra session for Command Center; never substitutes the existing Vector login. */
export const CC_STORAGE_KEY = "vector_command_center_session_v1";
const CC_TAB_KEY = "vector_command_center_session_key_v1";
const LOCK_NAME = "vector-command-center-cookie-v1";
const RENEW_BEFORE_MS = 120000;

export async function deriveCommandCenterSessionKey(jwt, cryptoApi = globalThis.crypto) {
  if (!cryptoApi?.subtle || typeof jwt !== "string" || jwt.length > 12288) {
    throw new Error("Web Crypto no está disponible");
  }
  const bytes = new TextEncoder().encode("header:v1:" + jwt);
  const digest = new Uint8Array(await cryptoApi.subtle.digest("SHA-256", bytes));
  return btoa(String.fromCharCode(...digest)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Old service workers can serve HTML without frame-ancestors; fail closed. */
export async function hasSafeCommandCenterServiceWorker(browser = window) {
  const sw = browser.navigator?.serviceWorker;
  // Without a proven new controller, the browser HTTP cache could still serve
  // old portal HTML (without frame-ancestors) inside the remote iframe.
  if (!sw) return false;
  const registrations = await sw.getRegistrations();
  const active = registrations.filter((registration) =>
    browser.location.href.startsWith(registration.scope) && registration.active);
  if (active.length === 0) return false;
  const controller = sw.controller;
  if (!controller || active.length !== 1 || active[0].active !== controller) return false;
  const nonce = Array.from(browser.crypto.getRandomValues(new Uint8Array(16)),
    (byte) => byte.toString(16).padStart(2, "0")).join("");
  return new Promise((resolve) => {
    const timer = browser.setTimeout(() => finish(false), 1500);
    function finish(ok) {
      browser.clearTimeout(timer);
      sw.removeEventListener("message", onMessage);
      resolve(ok && sw.controller === controller);
    }
    function onMessage(event) {
      const data = event.data;
      if (event.source !== controller || data?.type !== "VECTOR_SW_CACHE_NAME" ||
          data.proofNonce !== nonce) return;
      finish(data.ccNavigationNetworkOnly === true &&
        /^\d+$/.test(data.cacheNumber) && Number(data.cacheNumber) >= 511);
    }
    sw.addEventListener("message", onMessage);
    controller.postMessage({ type: "GET_CACHE_NAME", proofNonce: nonce });
  });
}

export function isCurrentCommandCenterSession(session, browser = window) {
  if (!session || session.status !== "ready" ||
      Date.parse(session.expiresAt) <= Date.now() + 30000) return false;
  try {
    const stored = JSON.parse(browser.localStorage.getItem(CC_STORAGE_KEY) || "null");
    return stored && stored.jwt === session.jwt && stored.subject === session.subject &&
      stored.version === session.version &&
      browser.sessionStorage.getItem(CC_TAB_KEY) === session.sessionKey;
  } catch (_error) { return false; }
}

export function createCommandCenterSessionController({ socket, store, browser = window,
  fetcher = (...args) => browser.fetch(...args),
  securityCheck = () => hasSafeCommandCenterServiceWorker(browser) }) {
  let authenticated = false;
  let disposed = false;
  let generation = 0;
  let subject = "";
  let renewalTimer = null;
  let retryTimer = null;
  const channel = typeof browser.BroadcastChannel === "function"
    ? new browser.BroadcastChannel("vector-command-center-session-v1") : null;

  function clearTimers() {
    browser.clearTimeout(renewalTimer);
    browser.clearTimeout(retryTimer);
    renewalTimer = retryTimer = null;
  }

  function setIdle(status = "idle", error = "") {
    store.set({ status, error, jwt: "", sessionKey: "", subject: "", expiresAt: "", version: 0 });
  }

  function readStored() {
    try {
      const raw = browser.localStorage.getItem(CC_STORAGE_KEY);
      if (!raw || raw.length > 16000) return null;
      const data = JSON.parse(raw);
      if (!data || typeof data.jwt !== "string" ||
          typeof data.subject !== "string" || !Number.isInteger(data.version) ||
          !Number.isFinite(Date.parse(data.expiresAt)) ||
          !/^[!#$%&'*+.^_`|~0-9A-Za-z-]{1,64}$/.test(data.headerName)) return null;
      return data;
    } catch (_error) { return null; }
  }

  function announceChange() {
    try { channel?.postMessage({ type: "changed" }); } catch (_error) {}
  }

  async function socketRequest(event) {
    if (!socket.connected) throw new Error("La conexión con Vector no está lista");
    return new Promise((resolve, reject) => {
      const timeout = browser.setTimeout(() => reject(new Error("Sin respuesta de Vector")), 8000);
      socket.emit(event, (reply) => {
        browser.clearTimeout(timeout);
        if (reply?.ok) resolve(reply);
        else reject(new Error(reply?.error || "Sesión federada no disponible"));
      });
    });
  }

  async function post(path, body, headers = {}, timeoutMs = 8000) {
    const controller = new AbortController();
    const timer = browser.setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetcher(path, {
        method: "POST", mode: "same-origin", credentials: "include", redirect: "error",
        cache: "no-store", referrerPolicy: "no-referrer", signal: controller.signal,
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify(body),
      });
      const result = await response.json();
      if (!response.ok || !result.ok) throw new Error(result.error || "Sesión federada no disponible");
      return result;
    } finally {
      browser.clearTimeout(timer);
    }
  }

  function useSession(saved, sessionKey) {
    if (readStored()?.jwt !== saved.jwt) throw new Error("Otra pestaña cambió la sesión");
    browser.sessionStorage.setItem(CC_TAB_KEY, sessionKey);
    const ready = { status: "ready", error: "", jwt: saved.jwt, sessionKey,
      subject: saved.subject, expiresAt: saved.expiresAt, version: saved.version };
    store.set(ready);
    const untilRenewal = Date.parse(saved.expiresAt) - Date.now() - RENEW_BEFORE_MS;
    renewalTimer = browser.setTimeout(() => authenticate(), Math.max(1000, untilRenewal));
  }

  async function withLock(fn) {
    if (!browser.navigator?.locks?.request) {
      throw new Error("La sincronización entre pestañas no está disponible");
    }
    return browser.navigator.locks.request(LOCK_NAME, { mode: "exclusive" }, fn);
  }

  function current(mark) { return authenticated && !disposed && generation === mark && socket.connected; }

  async function authenticate() {
    if (!authenticated || disposed) return;
    const mark = ++generation;
    clearTimers();
    setIdle("loading");
    try {
      const status = await socketRequest("command_center_session_status");
      if (!current(mark)) return;
      subject = status.subject;
      if (!status.enabled) {
        browser.localStorage.removeItem(CC_STORAGE_KEY);
        browser.sessionStorage.removeItem(CC_TAB_KEY);
        setIdle("disabled");
        return;
      }
      if (!await securityCheck()) {
        // Even without postMessage, an iframe that reaches old same-origin
        // HTML could read the parent's localStorage.
        browser.localStorage.removeItem(CC_STORAGE_KEY);
        browser.sessionStorage.removeItem(CC_TAB_KEY);
        throw new Error("El service worker seguro debe controlar esta página antes de emitir la sesión");
      }
      if (!current(mark)) return;
      await withLock(async () => {
        if (!current(mark)) return;
        if (!await securityCheck()) {
          browser.localStorage.removeItem(CC_STORAGE_KEY);
          browser.sessionStorage.removeItem(CC_TAB_KEY);
          throw new Error("El service worker seguro ya no controla esta página");
        }
        if (!current(mark)) return;
        const saved = readStored();
        if (saved && saved.subject === subject && saved.version === status.version &&
            Date.parse(saved.expiresAt) > Date.now() + RENEW_BEFORE_MS) {
          const sessionKey = await deriveCommandCenterSessionKey(saved.jwt, browser.crypto);
          try {
            await post("/api/session/check", { jwt: saved.jwt, data: {} },
              { [saved.headerName]: sessionKey });
            if (current(mark)) useSession(saved, sessionKey);
            return;
          } catch (_error) { /* Invalid cookie, expiration or rotation: reissue. */ }
        }
        if (!current(mark)) return;
        const challenge = await socketRequest("command_center_session_challenge");
        if (!current(mark)) return;
        if (challenge.subject !== subject || challenge.version !== status.version) {
          throw new Error("La configuración de sesión cambió; se volverá a intentar");
        }
        const issued = await post("/api/session", {}, { "X-Session-Challenge": challenge.ticket });
        const sessionKey = await deriveCommandCenterSessionKey(issued.jwt, browser.crypto);
        if (!await securityCheck() || !current(mark) || issued.user?.sub !== subject ||
            issued.version !== status.version) {
          await post("/api/session/clear", { jwt: issued.jwt, data: {} },
            { [status.headerName]: sessionKey }).catch(() => {});
          return;
        }
        await post("/api/session/check", { jwt: issued.jwt, data: {} },
          { [status.headerName]: sessionKey });
        if (!current(mark)) {
          await post("/api/session/clear", { jwt: issued.jwt, data: {} },
            { [status.headerName]: sessionKey }).catch(() => {});
          return;
        }
        const next = { jwt: issued.jwt, subject, expiresAt: issued.expiresAt,
          version: issued.version, headerName: status.headerName };
        browser.localStorage.setItem(CC_STORAGE_KEY, JSON.stringify(next));
        announceChange();
        useSession(next, sessionKey);
      });
    } catch (error) {
      if (!current(mark)) return;
      setIdle("error", error.message || "Sesión federada no disponible");
      retryTimer = browser.setTimeout(() => authenticate(), 15000);
    }
  }

  function start() {
    authenticated = true;
    return authenticate();
  }

  function suspend() {
    authenticated = false;
    generation++;
    clearTimers();
    setIdle();
  }

  async function logout({ forceLocalClear = false } = {}) {
    suspend();
    const before = readStored();
    try {
      await withLock(async () => {
        const saved = readStored();
        if (before && saved?.jwt === before.jwt) {
          if (before.subject === subject) {
            const key = await deriveCommandCenterSessionKey(saved.jwt, browser.crypto);
            await post("/api/session/clear", { jwt: saved.jwt, data: {} },
              { [saved.headerName]: key }, 2000).catch(() => {});
          }
          if ((before.subject === subject || forceLocalClear) && readStored()?.jwt === saved.jwt) {
            browser.localStorage.removeItem(CC_STORAGE_KEY);
          }
        }
      });
    } catch (_error) {
      // No ejecutar logout HTTP sin lock: una cookie de otra pestaña podría borrarse.
      if (before && (before.subject === subject || forceLocalClear) &&
          readStored()?.jwt === before.jwt) {
        browser.localStorage.removeItem(CC_STORAGE_KEY);
      }
    }
    browser.sessionStorage.removeItem(CC_TAB_KEY);
    announceChange();
  }

  function onOtherTab() {
    if (!authenticated || disposed) return;
    const stored = readStored();
    if (!stored || stored.subject !== subject) {
      suspend();
    } else {
      authenticate();
    }
  }

  function onStorage(event) {
    if (event.key === CC_STORAGE_KEY) onOtherTab();
  }

  function onVisibility() {
    if (authenticated && browser.document.visibilityState === "visible") authenticate();
  }

  browser.addEventListener("storage", onStorage);
  browser.document.addEventListener("visibilitychange", onVisibility);
  if (channel) channel.onmessage = (event) => {
    if (event.data?.type === "changed") onOtherTab();
  };

  return {
    start, suspend, logout, authenticate,
    dispose() {
      suspend();
      disposed = true;
      browser.removeEventListener("storage", onStorage);
      browser.document.removeEventListener("visibilitychange", onVisibility);
      channel?.close();
    },
  };
}